<template>
  <UploadStatusList :show="showDialog" :statuses="uploadStatuses" />
</template>

<script>
import { nanoid } from 'nanoid';
import { mapGetters, mapActions, mapMutations } from 'vuex';
import { store, utils } from 'editor-svelte';
import UploadStatusList from '@/shared/components/upload/UploadStatusList';
import { types as uploadTypes } from '@/shared/store/upload';

export default {
  name: 'EditorFileUploader',
  components: {
    UploadStatusList,
  },
  props: {
    basePath: {
      type: String,
      required: true,
    },
    showProgressDialog: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      unsubscribeUploadedFile: undefined,
      showDialog: false,
      uploadStatuses: [],
      currentFile: '',
    };
  },
  computed: {
    ...mapGetters({
      uploadInProgress: uploadTypes.getters.uploadInProgress,
      uploadedFile: uploadTypes.getters.uploadedFile,
      uploadProgressState: uploadTypes.getters.uploadProgressState,
      uploadedFileDownloadUrl: uploadTypes.getters.uploadedFileDownloadUrl,
      uploadSuccess: uploadTypes.getters.uploadSuccess,
    }),
    uploadError() {
      return this.uploadedFile instanceof Error;
    },
    completed() {
      return this.uploadSuccess || this.uploadError;
    },
  },
  mounted() {
    this.unsubscribeUploadedFile = store.file.filesToUpload.subscribe(async message => {
      if (!message) return;

      if (this.currentFile) {
        console.warn('File upload already in progress, skipping. Message: ', message);
        return;
      }

      this.resetAll();

      this.uploadStatuses = message.files.map((file) => ({ filename: file.name, progress: 0 }));

      if (this.showProgressDialog) {
        this.showDialog = true;
      }

      // eslint-disable-next-line
      for (const file of message.files) {
        this.currentFile = file.name;
        // eslint-disable-next-line
        const { url, name } = await this.uploadFile(file);

        if (utils.image.isImage(file)) {
          store.file.insertImage({ src: url, title: name, position: message.position });
        }
      }

      this.resetAll();
    });
  },
  destroyed() {
    this.unsubscribeUploadedFile();
  },
  methods: {
    ...mapActions({
      uploadFileAction: uploadTypes.actions.uploadFile,
      fetchDownloadUrl: uploadTypes.actions.fetchDownloadUrl,
    }),
    ...mapMutations({
      resetUploadState: uploadTypes.mutations.resetUploadState,
    }),
    resetCurrentUpload() {
      this.resetUploadState();
      this.currentFile = '';
    },
    resetAll() {
      this.resetCurrentUpload();
      this.showDialog = false;
      this.uploadStatuses = [];
    },
    async uploadFile(file) {
      await this.uploadFileAction({
        file,
        title: file.name,
        path: `${this.basePath}/${nanoid(16)}`,
      });

      // TODO @Reviewer : is this way to wait for variable change ok...???
      async function waitForCompletion() {
        const wait = (ms) => new Promise((res) => setTimeout(res, ms));
        while (!this.completed) {
          // eslint-disable-next-line no-await-in-loop
          await wait(100);
        }
      }
      await waitForCompletion.bind(this)();

      if (!this.uploadSuccess) {
        throw new Error(`Failed to upload file ${file.name}`);
      }

      await this.fetchDownloadUrl();

      const downloadUrl = this.uploadedFileDownloadUrl;

      this.resetCurrentUpload();

      return { url: downloadUrl, name: file.name };
    },
  },
  watch: {
    uploadProgressState(uploadProgressState) {
      if (!this.currentFile) return;

      const status = this.uploadStatuses.find(uploadStatus => uploadStatus.filename === this.currentFile);

      if (!status) return;

      status.progress = uploadProgressState;
    },
  },
};
</script>
